/**
 * The AI-ledger half of the KnowIt adapter: `/api/ai.usage`, `/api/ai.calls` and
 * `/api/queues.digest`.
 *
 * Like the automation routes, these answer bespoke objects rather than the `{ data,
 * policies }` envelope, so their translation lives beside `knowitAutomation.ts` and is handed
 * the same transport instead of reaching into the main adapter class. The three calls ride
 * the adapter's single request path — bearer injected, 401 refreshed — because a raw fetch
 * from a component arrives anonymous (the World-page outage of 2026-08-18).
 *
 * Two normalisations are deliberate and load-bearing:
 *
 * - **Estimates stay flagged.** The server prices provenance rows at the model's output rate
 *   and marks the rate `known: false` when it fell back; measured and estimated token sums
 *   arrive apart. This module keeps them apart (`rateKnown`, `measuredTokens` vs
 *   `estimatedTokens`, `tokensMeasured`) — folding them together would republish chars/4
 *   guesses as data.
 * - **`reading` survives.** The server sends its own caveats as an object of named sentences
 *   ("engine pricing is a placeholder rate", …). They flatten to `string[]` here and the UI
 *   renders them, because a cost figure whose caveat was dropped is a different, wrong claim.
 */
import type { AiCallsQuery, AiUsageQuery } from "~/api/client";
import { bool, list, num, numOrNull, obj, str, strOrNull } from "~/api/knowitAutomation";
import type {
  AiActionsModelRow,
  AiCall,
  AiCallsPage,
  AiEngineRow,
  AiPulseModelRow,
  AiStageRow,
  AiUsage,
  QueueDigest,
} from "~/api/types";

/** What the AI surface needs from the transport it is mounted on. */
export interface AiTransport {
  post(path: string, body?: Record<string, unknown>): Promise<unknown>;
}

/** The server's `reading` is an object of named sentences; order is the server's own. */
function sentences(value: unknown): string[] {
  return Object.values(obj(value)).filter(
    (entry): entry is string => typeof entry === "string" && entry.length > 0,
  );
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

/** `rate: { match, known, output }` — only the flag matters to the UI; absent = unknown. */
function rateKnown(value: unknown): boolean {
  return bool(obj(value).known);
}

export function createKnowItAi(transport: AiTransport) {
  const toPulseModel = (entry: Record<string, unknown>): AiPulseModelRow => ({
    model: str(entry.model, "(unrecorded)"),
    thinkerIds: strings(entry.thinkerIds),
    calls: num(entry.calls),
    failures: num(entry.failures),
    measuredTokens: num(entry.measuredTokens),
    estimatedTokens: num(entry.estimatedTokens),
    durationMs: num(entry.durationMs),
    costMicros: num(entry.costMicros),
    rateKnown: rateKnown(entry.rate),
  });

  const toActionsModel = (entry: Record<string, unknown>): AiActionsModelRow => ({
    model: str(entry.model, "(unrecorded)"),
    thinkerIds: strings(entry.thinkerIds),
    calls: num(entry.calls),
    inputTokens: num(entry.inputTokens),
    cacheWriteTokens: num(entry.cacheWriteTokens),
    cacheReadTokens: num(entry.cacheReadTokens),
    outputTokens: num(entry.outputTokens),
    costMicros: num(entry.costMicros),
    rateKnown: rateKnown(entry.rate),
  });

  const toStage = (entry: Record<string, unknown>): AiStageRow => ({
    stage: str(entry.stage, "(unrecorded)"),
    calls: num(entry.calls),
    failures: num(entry.failures),
    tokens: num(entry.tokens),
    models: strings(entry.models),
  });

  const toEngine = (entry: Record<string, unknown>): AiEngineRow => ({
    engine: str(entry.engine, "(unrecorded)"),
    kind: str(entry.kind),
    runs: num(entry.runs),
    failed: num(entry.failed),
    durationMs: num(entry.durationMs),
    costMicros: num(entry.costMicros),
  });

  const toCall = (entry: Record<string, unknown>): AiCall => ({
    at: str(entry.at, new Date(0).toISOString()),
    lane: str(entry.lane),
    what: str(entry.what),
    thinkerId: strOrNull(entry.thinkerId),
    model: strOrNull(entry.model),
    sessionId: strOrNull(entry.sessionId),
    flow: strOrNull(entry.flow),
    target: strOrNull(entry.target),
    tokensIn: numOrNull(entry.tokensIn),
    tokensOut: numOrNull(entry.tokensOut),
    tokensTotal: numOrNull(entry.tokensTotal),
    tokensMeasured: bool(entry.tokensMeasured),
    durationMs: num(entry.durationMs),
    costMicros: num(entry.costMicros),
    // A missing outcome is stated as unknown rather than dressed up as success.
    outcome: str(entry.outcome, "(unrecorded)"),
  });

  return {
    usage: async (query: AiUsageQuery = {}): Promise<AiUsage> => {
      const raw = obj(
        await transport.post("ai.usage", query.days === undefined ? {} : { days: query.days }),
      );
      const totals = obj(raw.totals);
      const lanes = obj(raw.lanes);
      const pulse = obj(lanes.pulse);
      const actions = obj(lanes.actions);
      const billing = obj(lanes.billing);
      const engines = obj(lanes.engines);
      const costMicros = num(totals.costMicros);

      return {
        windowDays: num(raw.windowDays, 30),
        totals: {
          calls: num(totals.calls),
          llmTokens: num(totals.llmTokens),
          costMicros,
          costUsd: num(totals.costUsd, costMicros / 1_000_000),
        },
        pulse: {
          grain: str(pulse.grain),
          calls: num(pulse.calls),
          measuredTokens: num(pulse.measuredTokens),
          estimatedTokens: num(pulse.estimatedTokens),
          costMicros: num(pulse.costMicros),
          byModel: list(pulse.byModel).map(toPulseModel),
          byStage: list(pulse.byStage).map(toStage),
        },
        actions: {
          grain: str(actions.grain),
          calls: num(actions.calls),
          inputTokens: num(actions.inputTokens),
          cacheWriteTokens: num(actions.cacheWriteTokens),
          cacheReadTokens: num(actions.cacheReadTokens),
          outputTokens: num(actions.outputTokens),
          costMicros: num(actions.costMicros),
          byModel: list(actions.byModel).map(toActionsModel),
        },
        billing: {
          grain: str(billing.grain),
          tokensIn: num(billing.tokensIn),
          tokensOut: num(billing.tokensOut),
          bySource: list(billing.bySource).map((entry) => ({
            source: str(entry.source, "(unrecorded)"),
            tokens: num(entry.tokens),
          })),
        },
        engines: {
          grain: str(engines.grain),
          runs: num(engines.runs),
          costMicros: num(engines.costMicros),
          engines: list(engines.engines).map(toEngine),
        },
        byFlow: list(raw.byFlow).map((entry) => ({
          flow: str(entry.flow, "(unnamed run)"),
          calls: num(entry.calls),
          tokens: num(entry.tokens),
          costMicros: num(entry.costMicros),
        })),
        daily: list(raw.daily).map((entry) => ({
          day: str(entry.day),
          calls: num(entry.calls),
          tokens: num(entry.tokens),
          costMicros: num(entry.costMicros),
        })),
        reading: sentences(raw.reading),
      };
    },

    calls: async (query: AiCallsQuery = {}): Promise<AiCallsPage> => {
      const raw = obj(
        await transport.post("ai.calls", {
          ...(query.days === undefined ? {} : { days: query.days }),
          ...(query.limit === undefined ? {} : { limit: query.limit }),
          ...(query.lane ? { lane: query.lane } : {}),
        }),
      );
      return {
        windowDays: num(raw.windowDays, 7),
        calls: list(raw.calls).map(toCall),
        reading: sentences(raw.reading),
      };
    },

    queueDigest: async (): Promise<QueueDigest> => {
      const raw = obj(await transport.post("queues.digest", {}));
      const backlog = obj(raw.backlog);
      const drain = obj(raw.drain);
      // `queue: null` is a state (inline transport, or the broker refused the probe), not an
      // absence to paper over with zeros — depth 0 and "unknown" are different claims.
      const queue = raw.queue === null || raw.queue === undefined ? null : obj(raw.queue);
      const band = queue === null ? null : obj(queue.band);

      return {
        transport: str(raw.transport) === "queue" ? "queue" : "inline",
        payloadEncryption: bool(raw.payloadEncryption),
        backlog: { pendingArticles: num(backlog.pendingArticles), note: str(backlog.note) },
        queue:
          queue === null
            ? null
            : {
                name: str(queue.name),
                messages: num(queue.messages),
                consumers: num(queue.consumers),
                band: {
                  min: num(band?.min),
                  max: num(band?.max),
                  scaleUpAt: num(band?.scaleUpAt),
                },
              },
        deadLetters: num(raw.deadLetters),
        drain: { lastHour: num(drain.lastHour), lastDay: num(drain.lastDay) },
      };
    },
  };
}
